import { ConfidenceLevelData, IEvidence } from "../types/Interfaces";

export namespace ConfidenceGraphUtils {
  /**
   *
   * @param progressCheckCompetenceID - The ID of the current competence
   * @returns true if the ID is included in the list of competences for the confidence level entry
   */
  export function competenceIsConfidenceLevel(progressCheckCompetenceID: string): boolean {
    return [
      // SWASFT Preceptorship
      "LNA36J9UUQHQ0",
      "LNA36J9UUQHQ1",
      "LNA36J9UUQHQ4",
      "LNA36J9UUQHQ1",
      "LNA36J9UUQHQ6",
      "LNA36J9UUQHQ1",
      "LNA37J9UUQHQ1",
      // NEAS NQP
      "M6Z8HDW2KEF3A", // 0-6 months
      "M6Z8IULAA8IJO", // 6-12 months
      "M6Z8JJ6E3JDOG", // 12-18 months
      "M6Z8JYY5VHR3L", // 18-24 months
      "M6Z8CKE5KO1BE", // General / Overall confidence level
      // YAS NQP
      "M7EPWG2P6KW3G", // 0-6 months
      "M7EPWG72QCNYG", // 6-12 months
      "M7EPWGCMJH70D", // 12-18 months,
      "M7EPWGIQCD92P", // 18-24 months
      "M7KS5NOVZ496O", // TL Observation Shift supervision
      // LAS NQP
      "MBT82WLM3SQK3", // 6-month Review
      "MBT90OE22KXDE", // 12-month Review
      "MBT91TYU34YNH", // 18-month Review
      "MBT92GP7IC722", // 24-month Review
    ].includes(progressCheckCompetenceID);
  }

  /**
   * Retrieves all the evidence required to create the confidence level graph
   * @param programEvidence - All the evidence for the current program
   * @returns
   */
  export function getConfidenceLevelEvidence(programEvidence: IEvidence[]) {
    let evidenceWithConfidenceLevels: IEvidence[] = [];

    evidenceWithConfidenceLevels = programEvidence.filter((evidence: IEvidence) => {
      const evidenceJSON = evidence.evidenceJSON && JSON.parse(evidence.evidenceJSON);
      const progressCheckCompetenceID = evidenceJSON?.programInfo?.progressCheckCompetenceID;

      return competenceIsConfidenceLevel(progressCheckCompetenceID);
    });

    return evidenceWithConfidenceLevels;
  }

  /**
   *
   * @param id - The ID of the progress check
   * @returns The index to insert the confidence level data into
   */
  export function getConfidenceLevelProgressCheckIndex(id: string): number {
    switch (id) {
      case "LNA4GMRKB031M":
      case "M167LOKBYZ1T9":
      case "M3SV5PZVW27GS": // NEAS NQP 0-6 months
      case "M7EPXK5EWRC2K": // YAS NQP 0-6 months
      case "M9BG9A6HSMOSV": // LAS NQP 6-month Review
        return 0;
      case "LNA4GMRKB041M":
      case "M167LOX3P35BG": // NEAS NQP 6-12 months
      case "M7EPXKAEBRMTC": // YAS NQP 6-12 months
      case "M9BG9AA7N3BE8": // LAS NQP 6-12 months
        return 1;
      case "LNA4GMRKB051M":
      case "M167LP2NL16ST": // NEAS NQP 12-18 months
      case "M7EPXKMQC1IRL": // YAS NQP 12-18 months
      case "M9BG9AS5V7GS4": // LAS NQP 12-18 months
        return 2;
      case "LNA4GMRKB081M":
      case "M167LP736CQKK": // NEAS NQP 18-24 months
      case "M7EPXKRMLVURA": // YAS NQP 18-24 months
      case "M9BG9AW1A3LRJ": // LAS NQP 18-24 months
        return 3;
      default:
        return -1;
    }
  }

  /**
   * Gets the evidence for generating the Preceptor confidence level graph and maps it to the ConfidenceLevelData object for the graph to use
   * @param confidenceLevelEvidence - The filtered evidence for the confidence level competences
   * @param programID
   * @param displayBlankGraph - Whether a mapped blank graph should be displayed or not
   * @returns The mapped confidence level data if the first required evidence has been added, or null if it has not been added
   */
  export function mapConfidenceLevelEvidence(
    confidenceLevelEvidence: IEvidence[],
    programID: string,
    displayBlankGraph?: boolean
  ): ConfidenceLevelData | undefined {
    if (programID === "M167IR37RNSSQ") {
      // NEAS NQP
      const evidenceID = "M6Z8HDW2KEF3A";

      const prePreceptorEvidence = confidenceLevelEvidence.find((evidence: IEvidence) => {
        const evidenceJSON = evidence.evidenceJSON && JSON.parse(evidence.evidenceJSON);
        const progressCheckCompetenceID = evidenceJSON?.programInfo?.progressCheckCompetenceID || "";

        const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

        return progressCheckCompetenceID === evidenceID && onHoldReset !== 1 && evidence.draft !== true;
      });

      if (prePreceptorEvidence) {
        const prePreceptorEvidenceJSON =
          prePreceptorEvidence.evidenceJSON && JSON.parse(prePreceptorEvidence.evidenceJSON);

        let area1Data = [0, 0, 0, 0];
        let area2Data = [0, 0, 0, 0];
        let generalData = [0, 0, 0, 0];

        for (let i = 0; i < confidenceLevelEvidence.length; i++) {
          const evidence = confidenceLevelEvidence[i];
          const evidenceJSON = evidence.evidenceJSON && JSON.parse(evidence.evidenceJSON);

          const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

          // Don't include draft evidence or evidence on hold reset in the data
          if (onHoldReset === 1 || evidence.draft === true) {
            continue;
          }

          const index = getConfidenceLevelProgressCheckIndex(evidenceJSON.programInfo?.progressCheckID);

          if (index > -1) {
            if (evidenceJSON.programInfo.progressCheckCompetenceID === "M6Z8HDW2KEF3A") {
              area1Data[index] = parseInt(evidenceJSON["Confidence level 1"], 10);
              area2Data[index] = parseInt(evidenceJSON["Confidence level 2"], 10);
              generalData[index] = parseInt(evidenceJSON["General Confidence level"], 10);
            } else {
              area1Data[index] = parseInt(evidenceJSON["Confidence level 1"], 10);
              area2Data[index] = parseInt(evidenceJSON["Confidence level 2"], 10);
              generalData[index] = parseInt(evidenceJSON["General Confidence level"], 10);
            }
          }
        }

        return {
          area1: {
            areaName: prePreceptorEvidenceJSON["Area of practice 1 - pre-preceptorship"],
            data: area1Data,
          },
          area2: {
            areaName: prePreceptorEvidenceJSON["Area of practice 2 - pre-preceptorship"],
            data: area2Data,
          },
          general: {
            areaName: "General",
            data: generalData,
          },
        };
      }

      if (displayBlankGraph) {
        return {
          area1: {
            areaName: "Area 1",
            data: [0, 0, 0, 0],
          },
          area2: {
            areaName: "Area 2",
            data: [0, 0, 0, 0],
          },
          general: {
            areaName: "General",
            data: [0, 0, 0, 0],
          },
        };
      }

      return undefined;
    } else if (programID === "M7EPWBZE8P2XK") {
      // YAS NQP
      const evidenceID = "M7EPWG2P6KW3G";
      const TLEvidenceID = "M7KS5NOVZ496O";

      const prePreceptorEvidence = confidenceLevelEvidence.find((evidence: IEvidence) => {
        const evidenceJSON = evidence.evidenceJSON && JSON.parse(evidence.evidenceJSON);
        const progressCheckCompetenceID = evidenceJSON?.programInfo?.progressCheckCompetenceID || "";

        const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

        return progressCheckCompetenceID === evidenceID && onHoldReset !== 1 && evidence.draft !== true;
      });

      const TLShiftEvidence = confidenceLevelEvidence.find((evidence: IEvidence) => {
        const evidenceJSON = evidence.evidenceJSON && JSON.parse(evidence.evidenceJSON);
        const progressCheckCompetenceID = evidenceJSON?.programInfo?.progressCheckCompetenceID || "";

        const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

        return progressCheckCompetenceID === TLEvidenceID && onHoldReset !== 1 && evidence.draft !== true;
      });

      if (prePreceptorEvidence || TLShiftEvidence) {
        const prePreceptorEvidenceJSON =
          prePreceptorEvidence && prePreceptorEvidence.evidenceJSON && JSON.parse(prePreceptorEvidence.evidenceJSON);

        // const TLShiftEvidenceJSON =
        //   TLShiftEvidence && TLShiftEvidence.evidenceJSON && JSON.parse(TLShiftEvidence.evidenceJSON);

        let area1Data = [0, 0, 0, 0];
        let area2Data = [0, 0, 0, 0];
        let generalData = [0, 0, 0, 0];
        let TLData = [0, 0, 0, 0];

        for (let i = 0; i < confidenceLevelEvidence.length; i++) {
          const evidence = confidenceLevelEvidence[i];
          const evidenceJSON = evidence.evidenceJSON && JSON.parse(evidence.evidenceJSON);

          const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

          // Don't include draft evidence or evidence on hold reset in the data
          if (onHoldReset === 1 || evidence.draft === true) {
            continue;
          }

          const index = getConfidenceLevelProgressCheckIndex(evidenceJSON.programInfo?.progressCheckID);

          if (index > -1) {
            if (evidenceJSON.programInfo.progressCheckCompetenceID === "M7EPWG2P6KW3G") {
              area1Data[index] = parseInt(evidenceJSON["Confidence level 1"], 10);
              area2Data[index] = parseInt(evidenceJSON["Confidence level 2"], 10);
              generalData[index] = parseInt(evidenceJSON["General Confidence level"], 10);
            } else if (evidenceJSON.programInfo.progressCheckCompetenceID === "M7KS5NOVZ496O") {
              TLData[index] = parseInt(evidenceJSON["TL Shift Confidence Levels"], 10);
            } else {
              area1Data[index] = parseInt(evidenceJSON["Confidence level 1"], 10);
              area2Data[index] = parseInt(evidenceJSON["Confidence level 2"], 10);
              generalData[index] = parseInt(evidenceJSON["General Confidence level"], 10);
            }
          }
        }

        return {
          area1: {
            areaName: prePreceptorEvidenceJSON?.["Area of practice 1 - pre-preceptorship"] ?? "Unset",
            data: area1Data,
          },
          area2: {
            areaName: prePreceptorEvidenceJSON?.["Area of practice 2 - pre-preceptorship"] ?? "Unset",
            data: area2Data,
          },
          general: {
            areaName: "General",
            data: generalData,
          },
          TLData: {
            areaName: "TL Shift",
            data: TLData,
          },
        };
      }

      if (displayBlankGraph) {
        return {
          area1: {
            areaName: "Area 1",
            data: [0, 0, 0, 0],
          },
          area2: {
            areaName: "Area 2",
            data: [0, 0, 0, 0],
          },
          general: {
            areaName: "General",
            data: [0, 0, 0, 0],
          },
          TLData: {
            areaName: "TL Shift",
            data: [0, 0, 0, 0],
          },
        };
      }

      return undefined;
    } else if (programID === "LNA4FTCKB012M") {
      // SWASFT Preceptorship NQP1
      const prePreceptorEvidenceID = "LNA36J9UUQHQ0";

      const prePreceptorEvidence = confidenceLevelEvidence.find((evidence: IEvidence) => {
        const evidenceJSON = evidence.evidenceJSON && JSON.parse(evidence.evidenceJSON);
        const progressCheckCompetenceID = evidenceJSON?.programInfo?.progressCheckCompetenceID ?? "";

        const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

        return progressCheckCompetenceID === prePreceptorEvidenceID && onHoldReset !== 1 && evidence.draft !== true;
      });

      if (prePreceptorEvidence) {
        const prePreceptorEvidenceJSON =
          prePreceptorEvidence.evidenceJSON && JSON.parse(prePreceptorEvidence.evidenceJSON);

        let area1Data = [0, 0, 0, 0];
        let area2Data = [0, 0, 0, 0];
        let generalData = [0, 0, 0, 0];

        for (let i = 0; i < confidenceLevelEvidence.length; i++) {
          const evidence = confidenceLevelEvidence[i];
          const evidenceJSON = evidence.evidenceJSON && JSON.parse(evidence.evidenceJSON);

          const onHoldReset = evidenceJSON.OnHoldReset ?? 0;

          if (onHoldReset === 1 || evidence.draft === true) {
            continue;
          }

          const index = getConfidenceLevelProgressCheckIndex(evidenceJSON.programInfo?.progressCheckID);

          if (index > -1) {
            if (evidenceJSON.programInfo?.progressCheckCompetenceID === "LNA36J9UUQHQ1") {
              generalData[index] = parseInt(evidenceJSON["Confidence level 1"]);
            } else if (evidenceJSON.programInfo?.progressCheckCompetenceID === "LNA37J9UUQHQ1") {
              area1Data[index] = parseInt(evidenceJSON["End of Preceptorship confidence levels 1"]);
              area2Data[index] = parseInt(evidenceJSON["End of Preceptorship confidence levels 2"]);
            } else {
              area1Data[index] = parseInt(evidenceJSON["Confidence level 1"]);
              area2Data[index] = parseInt(evidenceJSON["Confidence level 2"]);
            }
          }
        }

        return {
          area1: {
            areaName: prePreceptorEvidenceJSON["Area of practice 1 - pre-preceptorship"],
            data: area1Data,
          },
          area2: {
            areaName: prePreceptorEvidenceJSON["Area of practice 2 - pre-preceptorship"],
            data: area2Data,
          },
          general: {
            areaName: "General",
            data: generalData,
          },
        };
      }

      if (displayBlankGraph) {
        return {
          area1: {
            areaName: "Area 1",
            data: [0, 0, 0, 0],
          },
          area2: {
            areaName: "Area 2",
            data: [0, 0, 0, 0],
          },
          general: {
            areaName: "General",
            data: [0, 0, 0, 0],
          },
        };
      }

      return undefined;
    } else if (programID === "M9BG7MD6N63H0") {
      // LAS NQP
      const evidenceID = "MBT82WLM3SQK3";

      const firstConfidenceLevelEvidence = confidenceLevelEvidence.find((evidence: IEvidence) => {
        const evidenceJSON = evidence.evidenceJSON && JSON.parse(evidence.evidenceJSON);
        const progressCheckCompetenceID = evidenceJSON?.programInfo?.progressCheckCompetenceID || "";

        const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

        return progressCheckCompetenceID === evidenceID && onHoldReset !== 1 && evidence.draft !== true;
      });

      if (firstConfidenceLevelEvidence) {
        const firstConfidenceLevelEvidenceJSON = firstConfidenceLevelEvidence.evidenceJSON;

        let area1Data = [0, 0, 0, 0];
        let area2Data = [0, 0, 0, 0];
        let generalData = [0, 0, 0, 0];

        for (let i = 0; i < confidenceLevelEvidence.length; i++) {
          const evidence = confidenceLevelEvidence[i];
          const evidenceJSON = evidence.evidenceJSON && JSON.parse(evidence.evidenceJSON);

          const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

          if (onHoldReset === 1 || evidence.draft === true) {
            continue;
          }

          const index = getConfidenceLevelProgressCheckIndex(evidenceJSON.programInfo?.progressCheckID);

          if (index > -1) {
            if (evidenceJSON.programInfo.progressCheckCompetenceID === "MBT82WLM3SQK3") {
              area1Data[index] = parseInt(evidenceJSON["Confidence level 1"], 10);
              area2Data[index] = parseInt(evidenceJSON["Confidence level 2"], 10);
              generalData[index] = parseInt(evidenceJSON["General Confidence level"], 10);
            } else {
              area1Data[index] = parseInt(evidenceJSON["Confidence level 1"], 10);
              area2Data[index] = parseInt(evidenceJSON["Confidence level 2"], 10);
              generalData[index] = parseInt(evidenceJSON["General Confidence level"], 10);
            }
          }
        }

        return {
          area1: {
            areaName: firstConfidenceLevelEvidenceJSON["Area of practice 1 - pre-preceptorship"],
            data: area1Data,
          },
          area2: {
            areaName: firstConfidenceLevelEvidenceJSON["Area of practice 2 - pre-preceptorship"],
            data: area2Data,
          },
          general: {
            areaName: "General",
            data: generalData,
          },
        };
      }

      if (displayBlankGraph) {
        return {
          area1: {
            areaName: "Area 1",
            data: [0, 0, 0, 0],
          },
          area2: {
            areaName: "Area 2",
            data: [0, 0, 0, 0],
          },
          general: {
            areaName: "General",
            data: [0, 0, 0, 0],
          },
        };
      }

      return undefined;
    }

    return undefined;
  }
}
